import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { Sparkles, Shirt, Wand2, History, User, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { InstallAppBanner } from "@/components/InstallAppBanner";
import { InlineAdBanner } from "@/components/ads/InlineAdBanner";

import { useResumePendingGeneration } from "@/hooks/usePendingGeneration";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { isAndroidApp } from "@/lib/platform";
import { hideBanner, shouldShowInterstitial, showInterstitial } from "@/lib/native-ads";

const tabs = [
  { to: "/app/style", label: "Style", icon: Shirt },
  { to: "/app", label: "Try On", icon: Sparkles, end: true },
  { to: "/app/aura", label: "Aura", icon: Wand2 },
  { to: "/app/history", label: "History", icon: History },
  { to: "/app/profile", label: "Profile", icon: User },
];

export const AppLayout = () => {
  const { pathname } = useLocation();
  
  const isMobile = useIsMobile();
  // Resume any in-flight generation that survived a refresh / tab close.
  useResumePendingGeneration();
  // Persist collapsed state across navigations within the session.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("fitaura.sidebar.collapsed") === "1";
  });
  const toggleCollapsed = () => {
    setCollapsed((c) => {
      const next = !c;
      try { window.localStorage.setItem("fitaura.sidebar.collapsed", next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };

  // Persistent overlay banner is OFF — it was covering the bottom nav.
  // In-feed AdMob units now live inline at the bottom of each section's
  // content (rendered by the individual pages), so they scroll WITH the
  // page instead of floating above it.
  const { user } = useAuth();
  const [isPaid, setIsPaid] = useState<boolean | null>(null);
  useEffect(() => {
    if (!user) { setIsPaid(false); return; }
    supabase.from("profiles").select("credits_purchased_total").eq("user_id", user.id).maybeSingle()
      .then(({ data }) => setIsPaid(Number(data?.credits_purchased_total ?? 0) > 0));
  }, [user?.id]);

  // Make sure any banner that was shown by a previous build/version is hidden.
  useEffect(() => { void hideBanner(); }, []);

  // Interstitial between section transitions (free Android users, skip first nav).
  const firstNavRef = useRef(true);
  useEffect(() => {
    if (!isAndroidApp() || isPaid !== false) return;
    if (firstNavRef.current) { firstNavRef.current = false; return; }
    if (shouldShowInterstitial()) void showInterstitial();
  }, [pathname, isPaid]);

  return (
    <div className="relative flex min-h-screen text-foreground">
      {/* Ambient glow + faint grid — sits behind the whole authenticated app */}
      <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden bg-background">
        <div className="absolute -left-32 top-0 h-[420px] w-[420px] rounded-full bg-primary/15 blur-[140px]" />
        <div className="absolute -right-32 bottom-32 h-[420px] w-[420px] rounded-full bg-accent/10 blur-[140px]" />
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "linear-gradient(to right, hsl(var(--primary)) 1px, transparent 1px), linear-gradient(to bottom, hsl(var(--primary)) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
      </div>

      {/* Desktop sidebar (≥1024px) — collapses to icon-only mini variant */}
      {!isMobile && (
        <aside
          className={cn(
            "sticky top-0 z-10 hidden h-screen shrink-0 flex-col self-start border-r border-white/10 bg-card/50 backdrop-blur-xl transition-[width] duration-300 ease-out lg:flex",
            collapsed ? "w-[76px]" : "w-60",
          )}
        >
          <div className={cn("flex items-start pt-8 pb-6", collapsed ? "justify-center px-2" : "justify-between px-6")}>
            {!collapsed && (
              <div className="min-w-0">
                <span className="font-display block text-xl font-bold tracking-tight">
                  FitAura<span className="text-primary"> AI</span>
                </span>
                <span className="font-display mt-1 block text-[10px] font-semibold uppercase tracking-[0.28em] text-muted-foreground">
                  AI Styling Studio
                </span>
              </div>
            )}
            <button
              type="button"
              onClick={toggleCollapsed}
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-background/40 text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary"
            >
              {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            </button>
          </div>
          <nav className={cn("flex flex-1 flex-col gap-1", collapsed ? "px-2" : "px-3")}>
            {tabs.map(({ to, label, icon: Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                title={collapsed ? label : undefined}
                className={({ isActive }) =>
                  cn(
                    "flex items-center rounded-xl text-sm font-medium transition-colors",
                    collapsed ? "justify-center px-0 py-3" : "gap-3 px-3 py-2.5",
                    isActive
                      ? "bg-primary/15 text-primary"
                      : "text-muted-foreground hover:bg-white/5 hover:text-foreground",
                  )
                }
              >
                <Icon className="h-5 w-5 shrink-0" />
                {!collapsed && label}
              </NavLink>
            ))}
          </nav>
        </aside>
      )}

      {/* Main column */}
      <div className="relative z-10 flex min-w-0 flex-1 flex-col">
        <main className={cn("flex-1", isMobile && "pb-20")}>
          <Outlet />
          {/* End-of-section AdMob banner sentinel — when scrolled into view,
              shows the native overlay banner above the bottom nav. */}
          <InlineAdBanner />
        </main>

        {/* Mobile bottom nav (<1024px) — Material 3 compact (~56dp). */}
        {isMobile && (
          <nav
            className="fixed bottom-0 left-0 right-0 z-40 border-t border-white/10 bg-card/90 backdrop-blur-xl safe-bottom"
          >
            <div className="mx-auto flex h-14 max-w-md items-stretch justify-around px-1">
              {tabs.map(({ to, label, icon: Icon, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  className={({ isActive }) =>
                    cn(
                      "font-display flex flex-1 flex-col items-center justify-center gap-0.5 rounded-xl px-2 text-[9px] font-semibold uppercase tracking-[0.14em] transition-colors text-center",
                      isActive ? "text-primary" : "text-muted-foreground hover:text-foreground",
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span
                        className={cn(
                          "rounded-full p-1 transition-colors",
                          isActive
                            ? "bg-primary/15 text-primary shadow-[0_0_18px_-4px_hsl(var(--primary))]"
                            : "bg-transparent",
                        )}
                      >
                        <Icon className="h-[22px] w-[22px]" />
                      </span>
                      {label}
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          </nav>
        )}
      </div>

      {/* Web-only Play Store install banner — hidden on native shells. */}
      <InstallAppBanner />
    </div>
  );
};
